import * as Sentry from "@sentry/browser";
import {
  ContentBlock,
  ContentState,
  convertFromHTML,
  EditorState,
  Modifier,
  SelectionState,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";

import { EMAIL_WRAPPER_STYLE, EMPTY_EMAIL_HTML } from "./constants";
import { replaceCompanyNameWildcard } from "./utils";

/**
 * Converts email HTML to Draft.js EditorState
 *
 * Expected input format:
 * <div style="font-family: inherit; text-align: left">
 *   Text content<br><br>More content<br><br>Final content
 * </div>
 *
 * @param html - HTML string in email template format
 * @param companyName - Optional company name to replace {{ company_name }} wildcards
 * @returns EditorState for editing
 */
export const htmlToEditorState = (
  originalHtml: string,
  companyName?: string
): EditorState => {
  if (!originalHtml || originalHtml.trim() === "") {
    return EditorState.createEmpty();
  }

  try {
    let html = originalHtml;
    // Replace {{ company_name }} wildcard with actual company name before parsing
    if (companyName) {
      html = replaceCompanyNameWildcard(originalHtml, companyName);
    }
    // Strip the outer wrapper div
    const stripped = html
      .replace(/^<div[^>]*>/, "")
      .replace(/<\/div>$/, "")
      .trim();

    // First, parse HTML to find anchor tags with template variables
    // convertFromHTML might not create LINK entities for invalid URLs (like template variables)
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = stripped;
    const anchorTags: Array<{
      href: string;
      text: string;
    }> = [];

    // Find all anchor tags
    const anchors = tempDiv.querySelectorAll("a");
    anchors.forEach((anchor) => {
      const href = anchor.getAttribute("href") || "";
      const anchorText = anchor.textContent || "";
      if (href && anchorText) {
        anchorTags.push({
          href,
          text: anchorText,
        });
      }
    });

    // Convert HTML to Draft.js blocks
    // convertFromHTML should create LINK entities, but might skip template variables
    const blocksFromHTML = convertFromHTML(stripped);

    if (
      !blocksFromHTML.contentBlocks ||
      blocksFromHTML.contentBlocks.length === 0
    ) {
      return EditorState.createEmpty();
    }

    let contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );

    // Check each anchor tag and ensure it has a LINK entity
    // Find the text in the blocks and apply LINK entity if missing
    if (anchorTags.length > 0) {
      const blocks = contentState.getBlocksAsArray();

      anchorTags.forEach((anchorTag) => {
        // Find the block and offset that contains this anchor text
        let foundBlock: ContentBlock | null = null;
        let startOffset = -1;
        let endOffset = -1;

        blocks.forEach((block) => {
          const blockText = block.getText();
          const anchorTextIndex = blockText.indexOf(anchorTag.text);
          if (anchorTextIndex !== -1 && !foundBlock) {
            foundBlock = block;
            startOffset = anchorTextIndex;
            endOffset = anchorTextIndex + anchorTag.text.length;
          }
        });

        // If we found the text, check if it has a LINK entity
        if (foundBlock && startOffset !== -1) {
          const block: ContentBlock = foundBlock;
          const blockKey = block.getKey();
          let hasLinkEntity = false;
          const blockLength = block.getLength();

          // Check if this range already has a LINK entity
          for (let i = startOffset; i < endOffset && i < blockLength; i++) {
            const entityKey = block.getEntityAt(i);
            if (entityKey) {
              const entity = contentState.getEntity(entityKey);
              if (entity.getType() === "LINK") {
                hasLinkEntity = true;
                break;
              }
            }
          }

          // If no LINK entity exists, create one
          if (!hasLinkEntity) {
            const selection = SelectionState.createEmpty(blockKey).merge({
              anchorOffset: startOffset,
              focusOffset: endOffset,
            });

            // Create LINK entity
            const contentStateWithEntity = contentState.createEntity(
              "LINK",
              "MUTABLE",
              { url: anchorTag.href }
            );
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

            // Apply the entity to the selection
            contentState = Modifier.applyEntity(
              contentStateWithEntity,
              selection,
              entityKey
            );
          }
        }
      });
    }

    return EditorState.createWithContent(contentState);
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        html: originalHtml.substring(0, 500), // First 500 chars
        htmlLength: originalHtml.length,
        component: "EmailBodyEditor",
        function: "htmlToEditorState",
      },
      tags: {
        component: "EmailBodyEditor",
      },
    });

    // Return empty editor state as fallback
    return EditorState.createEmpty();
  }
};

/**
 * Converts Draft.js EditorState to email HTML
 *
 * Output format:
 * <div style="font-family: inherit; text-align: left">
 *   Text content<br><br>More content<br><br>Final content
 * </div>
 *
 * @param editorState - Current editor state
 * @returns HTML string in email template format
 */
export const editorStateToHtml = (editorState: EditorState): string => {
  const contentState = editorState.getCurrentContent();
  const blocks = contentState.getBlocksAsArray();

  if (blocks.length === 0 || !contentState.hasText()) {
    return EMPTY_EMAIL_HTML;
  }

  // Convert each block to HTML with inline styles
  const htmlBlocks = blocks.map((block) => {
    // Create a temporary ContentState with just this block
    const singleBlockContent = ContentState.createFromBlockArray([block]);

    // Convert to HTML with options to preserve inline styles (bold, italic, links)
    const blockHtml = stateToHTML(singleBlockContent, {
      // Don't wrap blocks in <p> tags
      defaultBlockTag: undefined,
    });

    // Remove any wrapping <p> tags that might have been added
    return blockHtml.replace(/^<p>|<\/p>$/g, "").trim();
  });

  // Remove empty blocks from start and end
  let startIndex = 0;
  let endIndex = htmlBlocks.length - 1;

  // Find first non-empty block
  while (startIndex <= endIndex && htmlBlocks[startIndex] === "") {
    startIndex += 1;
  }

  // Find last non-empty block
  while (endIndex >= startIndex && htmlBlocks[endIndex] === "") {
    endIndex -= 1;
  }

  // If all blocks were empty, return empty div
  if (startIndex > endIndex) {
    return EMPTY_EMAIL_HTML;
  }

  // Join blocks with appropriate break tags (single or double)
  const result: string[] = [];

  for (let i = startIndex; i <= endIndex; i++) {
    const currentBlock = htmlBlocks[i];
    result.push(currentBlock);

    // Don't add separator after last block
    if (i < endIndex) {
      const nextBlock = htmlBlocks[i + 1];

      // Check if current or next block is empty
      const isCurrentBlockEmpty =
        currentBlock === "" || currentBlock.trim() === "";
      const isNextBlockEmpty = nextBlock === "" || nextBlock.trim() === "";

      // If current block is empty, it represents a paragraph break (double Enter)
      // If next block is empty, a paragraph break is coming
      // Use double break for paragraph separation
      if (isCurrentBlockEmpty || isNextBlockEmpty) {
        result.push("<br><br>");
      } else {
        // Consecutive non-empty blocks = single line break
        result.push("<br>");
      }
    }
  }

  const innerHtml = result.join("");

  // Wrap in required div
  return `<div style="${EMAIL_WRAPPER_STYLE}">${innerHtml}</div>`;
};
